<template>
	<EventLayout :event="event" :subtitle="trans('events.blueprints.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-blueprint-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Modal id="create-blueprint-modal">
			<template #title>{{ trans('events.blueprints.add') }}</template>
			<LabeledObject required>
				<template #label>{{ trans('events.blueprints.name') }}</template>
				<input v-model="newBlueprint.title" type="text">
			</LabeledObject>
			<LabeledObject required>
				<template #label>{{ trans('events.blueprints.file') }}</template>
				<input type="file" accept="image/jpeg,image/png" @change="newBlueprint.file = loadFile($event)">
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="uploadBlueprint">
					<i class="fa-solid fa-upload mr-2"/>
					{{ trans('general.upload_file') }}
				</PrimaryButton>
			</template>
		</Modal>
		<Empty v-if="blueprints.length === 0"/>
		<div v-for="blueprint in blueprints" :key="`blueprint-${blueprint.id}`" class="relative">
			<img class="w-full rounded" :src="storage(blueprint.file as string)" :alt="trans('events.blueprints.title')">
			<p class="absolute bottom-0 left-0 w-full px-4 pb-2 pt-8 text-lg font-light text-white rounded-b bg-gradient-to-b from-0% to-90% from-transparent to-black">{{ blueprint.title }}</p>
			<div class="p-4 absolute top-0 right-0">
				<PrimaryButton :color="Color.Red" @click.stop="deleteBlueprint(blueprint.id)">
					<i class="fa-solid fa-trash"/>
				</PrimaryButton>
			</div>
		</div>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import {type Blueprint} from '@/Types/Events/Blueprint'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Event as AArEIIEvent} from '@/Types/Events/Event'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {loadFile, showInfo, showModal, showSuccess, showValidationErrors, storage} from '@/Helpers/general'

const props = defineProps<{
	event:AArEIIEvent,
}>()

const working:Ref<boolean> = ref(false)
const event:Ref<AArEIIEvent> = ref(props.event)
const blueprints:Ref<Array<Blueprint>> = ref(props.event.blueprints)
const newBlueprint:Ref<Blueprint> = ref({
	title: '',
	file: null,
} as Blueprint)

function uploadBlueprint():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		const formData = new FormData
		formData.append('title', newBlueprint.value.title)
		formData.append('file', newBlueprint.value.file ?? '')
		axios.post(route('events.blueprints.upload', event.value.id), formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}).then(({data}) => {
			blueprints.value.push(data)
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function deleteBlueprint(blueprintId:number):void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.blueprints.delete', blueprintId)).then(() => {
			const index = blueprints.value.findIndex((bp:Blueprint) => bp.id === blueprintId)
			if(index > -1){
				blueprints.value.splice(index, 1)
			}
			showSuccess(trans('general.deleted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>